import React, { useEffect, useRef, useState } from "react";
import { Button, Container, Collapse } from "reactstrap";
import { useTranslation } from "react-i18next";
import "./style.css";

const PersistentAudioPlayer = ({
  currentTrack,
  queue,
  currentIndex,
  isPlaying,
  lastUpdated,
  stopAudio,
  nextTrack,
  prevTrack,
  removeFromQueue,
  selectQueueTrack,
  setPlaying,
}) => {
  const { t } = useTranslation();
  const audioRef = useRef(null);
  const [showQueue, setShowQueue] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);

  useEffect(() => {
    if (audioRef.current && currentTrack) {
      audioRef.current.pause();
      audioRef.current.load();
      if (isPlaying) {
        audioRef.current.play().catch((error) => {
          console.warn("Audio playback failed:", error);
        });
      }
    }
  }, [currentTrack?.url, lastUpdated]);

  useEffect(() => {
    if (audioRef.current && currentTrack) {
      if (isPlaying) {
        audioRef.current.play().catch(() => {});
      } else {
        audioRef.current.pause();
      }
    }
  }, [isPlaying]);

  const handleTimeUpdate = () => {
    if (audioRef.current) {
      setCurrentTime(audioRef.current.currentTime);
    }
  };

  const handleLoadedMetadata = () => {
    if (audioRef.current) {
      setDuration(audioRef.current.duration);
    }
  };

  const handleSeek = (e) => {
    const time = parseFloat(e.target.value);
    if (audioRef.current) {
      audioRef.current.currentTime = time;
      setCurrentTime(time);
    }
  };

  const togglePlay = () => {
    if (audioRef.current) {
      if (audioRef.current.paused) {
        audioRef.current.play();
        setPlaying(true);
      } else {
        audioRef.current.pause();
        setPlaying(false);
      }
    }
  };

  const formatTime = (time) => {
    if (isNaN(time)) return "0:00";
    const minutes = Math.floor(time / 60);
    const seconds = Math.floor(time % 60);
    return `${minutes}:${seconds.toString().padStart(2, "0")}`;
  };

  if (!currentTrack) {
    return null;
  }

  const hasNext = currentIndex < queue.length - 1;
  const hasPrev = currentIndex > 0;

  return (
    <div className="persistent-audio-player">
      <Collapse isOpen={showQueue} className="queue-manager">
        <Container fluid className="px-3 py-3">
          <div className="d-flex justify-content-between align-items-center mb-3 queue-header">
            <h6 className="mb-0 fw-bold text-uppercase small letter-spacing-1">Up Next</h6>
            <Button color="link" className="p-0 text-muted" onClick={() => setShowQueue(false)}>
              <i className="fa fa-times"></i>
            </Button>
          </div>
          <div className="queue-list" style={{ maxHeight: "300px", overflowY: "auto" }}>
            {queue.map((track, i) => (
              <div
                key={`${track.url}-${i}`}
                className={`queue-item d-flex align-items-center justify-content-between rounded mb-1 ${
                  i === currentIndex ? "active-track" : ""
                }`}
              >
                <div 
                  className="d-flex flex-column flex-grow-1 cursor-pointer"
                  onClick={() => i !== currentIndex && selectQueueTrack(i)}
                >
                  <span className="track-name text-truncate">
                    {i === currentIndex && <i className="fa fa-volume-up me-2"></i>}
                    {track.title}
                  </span>
                  <span className="track-author text-truncate">{track.author}</span>
                </div>
                <div className="d-flex align-items-center">
                  {queue.length > 1 && (
                    <Button
                      color="link"
                      className="p-0 hover-danger ms-3"
                      onClick={(e) => {
                        e.stopPropagation();
                        removeFromQueue(i);
                      }}
                      title="Remove from queue"
                    >
                      <i className="fa fa-trash-o"></i>
                    </Button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </Container>
      </Collapse>

      <Container fluid className="px-4">
        <div className="custom-player-layout py-3">
          {/* Track Info */}
          <div className="track-info">
            <span className="track-title text-truncate" title={currentTrack.title}>
              {currentTrack.title}
            </span>
            <span className="track-author text-truncate">
              {currentTrack.author || "Unknown Author"}
            </span>
          </div>

          {/* Controls & Progress */}
          <div className="player-main-controls">
            <div className="playback-buttons mb-1">
              <Button
                color="link"
                className={`p-0 ${!hasPrev ? "opacity-25" : "text-dark"}`}
                onClick={(e) => { e.preventDefault(); prevTrack(); }}
                disabled={!hasPrev}
              >
                <i className="fa fa-step-backward"></i>
              </Button>
              <Button
                color="link"
                className="p-0 play-pause-btn mx-4"
                onClick={togglePlay}
                style={{ textDecoration: "none" }}
              >
                <i className={`fa ${isPlaying ? "fa-pause" : "fa-play"} fa-2x`}></i>
              </Button>
              <Button
                color="link"
                className={`p-0 ${!hasNext ? "opacity-25" : "text-dark"}`}
                onClick={(e) => { e.preventDefault(); nextTrack(); }}
                disabled={!hasNext}
              >
                <i className="fa fa-step-forward"></i>
              </Button>
            </div>

            <div className="progress-container">
              <span className="time-display">{formatTime(currentTime)}</span>
              <input
                type="range"
                className="progress-bar-custom"
                min="0"
                max={duration || 0}
                value={currentTime}
                onChange={handleSeek}
              />
              <span className="time-display">{formatTime(duration)}</span>
            </div>
          </div>

          {/* Extra Controls */}
          <div className="extra-controls">
            <Button
              color="link"
              className={`p-0 queue-toggle ${showQueue ? "text-primary" : ""}`}
              onClick={() => setShowQueue(!showQueue)}
              title={t("queue")}
            >
              <div>
              <i className="fa fa-list-ul fa-lg"></i>
              </div>
              <div>
              <span className="queue-toggle-label">{t("queue")}</span>
              {queue.length > 0 && (
                <span className="badge rounded-pill">
                  {queue.length}
                </span>
              )}
              </div>
            </Button>

            <Button
              color="link"
              className="p-0 close-btn ms-4"
              onClick={stopAudio}
              title="Close"
            >
              <i className="fa fa-times-circle fa-lg"></i>
            </Button>
          </div>
        </div>
      </Container>

      <audio
        ref={audioRef}
        src={currentTrack.url}
        onTimeUpdate={handleTimeUpdate}
        onLoadedMetadata={handleLoadedMetadata}
        onPlay={() => setPlaying(true)}
        onPause={() => setPlaying(false)}
        onEnded={() => {
          if (hasNext) {
            nextTrack();
          } else {
            stopAudio();
          }
        }}
      />
    </div>
  );
};

export default PersistentAudioPlayer;
